import { useCurrentWorkspace, useCustomQuery } from '@gripp/shared-logic';
import { SaveFormTop, TopModalBar, useGetDimensions } from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { EditFileAssetsList } from './EditFileAssetsList';

const GET_MEDIA_ASSIGNMENTS = `
  query GetMediaAssignments($mediaId: String!) {
    media {
      findMediaAssignments(mediaId: $mediaId) {
        assets
      }
    }
  }
`;

type Props = {
  fileId: string;
  title: string;
  showCheckboxColumn: boolean;
  onComplete: () => void;
};

export const FileAssetsForm = ({
  fileId,
  title,
  showCheckboxColumn,
  onComplete,
}: Props) => {
  const { t } = useTranslation();
  const { dimensions } = useGetDimensions();
  const height = dimensions?.height ?? 0;
  const queryClient = useQueryClient();
  const currentWorkspace = useCurrentWorkspace();

  const [isLoading, setIsLoading] = useState(false);

  const { data: mediaAssignments } = useCustomQuery({
    query: GET_MEDIA_ASSIGNMENTS,
    variables: { mediaId: fileId },
    cacheKey: ['mediaAssignments', fileId],
  });

  const handleSave = async () => {
    setIsLoading(true);
    try {
      // Refresh the files list to show updated counts
      await queryClient.invalidateQueries({ queryKey: ['media.listFiles'] });
      onComplete();
    } catch (error) {
      // Handle error silently or show user-friendly error message
      // console.error('Error saving file assets:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!currentWorkspace) {
    return null;
  }

  return (
    <SaveFormTop
      title={title}
      topBar={TopModalBar}
      onCancel={onComplete}
      onSubmit={handleSave}
      isDirty={false}
      isValid={true}
    >
      <View style={styles.container}>
        <EditFileAssetsList
          workspaceId={currentWorkspace.id}
          fileId={fileId}
          maxHeight={height - 200}
          showCheckboxColumn={showCheckboxColumn}
        />
      </View>
    </SaveFormTop>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'white',
  },
}); 